import React from 'react';

interface InvestorUnlockChartProps {
  tgeTime: number;
  cliffTime: number;
  monthlyDuration: number;
  trancheCount: number;
  now: number;
  unlockedPercent: number;
}

/** Investor timeline: TGE is informational; only the fixed unlock cliff releases tokens. */
export const InvestorUnlockChart: React.FC<InvestorUnlockChartProps> = ({
  tgeTime, cliffTime, monthlyDuration, trancheCount, now, unlockedPercent,
}) => {
  if (!cliffTime) {
    return <p className="sh-cause">Unlock schedule not set. The curve will appear once the unlock cliff is scheduled.</p>;
  }
  const W = 880, H = 240, PL = 40, PR = 24, PT = 32, PB = 36;
  const end = cliffTime + trancheCount * monthlyDuration;
  const origin = tgeTime > 0 ? Math.min(tgeTime, cliffTime) : cliffTime;
  const axisEnd = Math.max(end, tgeTime);
  const x = (t: number) => PL + ((t - origin) / Math.max(1, axisEnd - origin)) * (W - PL - PR);
  const y = (v: number) => H - PB - (v / 100) * (H - PT - PB);
  const date = (t: number) => new Date(t * 1000).toLocaleDateString(undefined, {
    month: 'short', day: '2-digit', year: 'numeric',
  });
  // Draw the cliff and monthly tranches as steps, so nothing appears unlocked early.
  const points = [{ t: origin, v: 0 }, { t: cliffTime, v: 0 }, { t: cliffTime, v: 25 }];
  for (let i = 1; i <= trancheCount; i++) {
    const t = cliffTime + i * monthlyDuration;
    points.push({ t, v: 25 + 75 * (i - 1) / trancheCount }, { t, v: 25 + 75 * i / trancheCount });
  }
  points.push({ t: axisEnd, v: 100 });
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t)},${y(p.v)}`).join(' ');
  const current = Math.max(origin, Math.min(axisEnd, now));
  const progress = Number.isFinite(unlockedPercent) ? Math.max(0, Math.min(100, unlockedPercent)) : 0;
  const todayLabel = `TODAY · ${progress.toFixed(1)}% UNLOCKED`;
  const flip = x(current) + 10 + todayLabel.length * 7.2 > W;
  const milestones = [
    ...(tgeTime > 0 ? [{ t: tgeTime, label: 'TGE', row: 0 }] : []),
    { t: cliffTime, label: 'UNLOCK CLIFF', row: tgeTime > 0 ? 1 : 0 },
  ];
  return (
    <svg role="img" aria-label="Investor unlock schedule from TGE and unlock cliff" viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" style={{ display: 'block' }}>
      {[0, 25, 50, 75, 100].map((v) => (
        <g key={v}>
          <line x1={PL} x2={W - PR} y1={y(v)} y2={y(v)} stroke="var(--border)" strokeDasharray="2 5" />
          <text x={PL - 8} y={y(v) + 3} fill="var(--text-muted)" fontSize="10" textAnchor="end">{v}%</text>
        </g>
      ))}
      {milestones.map(({ t, label, row }) => (
        <g key={label}>
          <line x1={x(t)} x2={x(t)} y1={PT} y2={H - PB} stroke="var(--info)" strokeDasharray="3 4" opacity="0.5" />
          <text x={x(t) + (x(t) > W / 2 ? -6 : 6)} y={PT + 10 + row * 15} textAnchor={x(t) > W / 2 ? 'end' : 'start'} fill="var(--info)" fontSize="10" fontFamily="var(--font-mono)">{label} · {date(t)}</text>
        </g>
      ))}
      {[0, 1, 2, 3, 4].map((i) => {
        const t = origin + (axisEnd - origin) * i / 4;
        return <text key={i} x={x(t)} y={H - 12} textAnchor={i === 0 ? 'start' : i === 4 ? 'end' : 'middle'} fill="var(--text-muted)" fontSize="10" fontFamily="var(--font-mono)">{date(t)}</text>;
      })}
      <path d={path} stroke="var(--info)" strokeWidth="1.5" fill="none" />
      <line x1={x(current)} x2={x(current)} y1={PT} y2={H - PB} stroke="var(--info)" opacity="0.5" />
      <circle cx={x(current)} cy={y(progress)} r="5" fill="var(--info)" stroke="var(--bg)" strokeWidth="2" />
      <text x={x(current) + (flip ? -10 : 10)} y={y(progress) - 10} textAnchor={flip ? 'end' : 'start'} fill="var(--text)" fontSize="11" fontFamily="var(--font-mono)">{todayLabel}</text>
    </svg>
  );
};
